import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PdfCanvasViewer from './PdfCanvasViewer';

/* Full-screen in-app document viewer, shown over the current page instead of
 * opening a new browser tab. Shared by the Work document pages and the B2B
 * dashboard (presentational only, no data-layer imports, so it's safe for both
 * bundles).
 *
 * Rendering inline like this — rather than window.open — keeps the document
 * behind the app's own session the whole time it's on screen: there's no
 * separate tab/URL that could be left open, bookmarked, or handed off
 * outside the authenticated session.
 *
 * PDFs go through PdfCanvasViewer rather than <iframe src="blob:…">. An iframe
 * pointed at a PDF blob still invokes Chrome's built-in PDF viewer — same
 * component, same "Summarize with Gemini" offer — so the in-app modal carried
 * the same exposure as the FSC tab did. Everything else keeps the iframe, which
 * is correct for images and HTML and harmless for types the browser just
 * downloads. */
export default function DocumentViewerModal({ url, filename, onClose, video = false }) {
  // Callers hand us a blob: URL they already fetched. For a PDF we need the
  // bytes, not a URL — read them back out of the blob, which is a local read,
  // not a network request, so the spent one-time token is not involved.
  //
  // `video` is the exception: the URL is a streaming playback link, not a
  // blob, and fetching it here would pull the whole video a second time.
  const [pdfData, setPdfData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setPdfData(null);
    if (!url || video) return undefined;
    (async () => {
      try {
        const resp = await fetch(url);           // blob: URL — in-memory
        const type = (resp.headers.get('Content-Type') || '').split(';')[0].trim().toLowerCase();
        if (type !== 'application/pdf') return;
        const buf = await resp.arrayBuffer();
        if (!cancelled) setPdfData(buf);
      } catch { /* fall back to the iframe below */ }
    })();
    return () => { cancelled = true; };
  }, [url, video]);

  if (!url) return null;
  return (
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: 'fixed', inset: 0, zIndex: 1400,
        background: 'rgba(10,41,71,0.65)',
        display: 'flex', flexDirection: 'column',
        padding: '1.5rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={filename || 'Document viewer'}
        style={{
          background: 'white', borderRadius: 8, flex: 1,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(10,41,71,0.35)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0.65rem 1rem', borderBottom: '1px solid #dde2e8', flexShrink: 0,
        }}>
          <span style={{
            fontWeight: 700, color: '#0a2947', fontSize: '0.9rem',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {filename || 'Document'}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 0, color: '#6b7280', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1, padding: '0.25rem' }}
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
        {video ? (
          <div style={{ flex: 1, minHeight: 0, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* nodownload: a save from the player menu would skip the
                show_download_buttons switch and the download count. */}
            <video
              src={url} controls autoPlay playsInline controlsList="nodownload"
              style={{ maxWidth: '100%', maxHeight: '100%' }}
            />
          </div>
        ) : pdfData
          ? <PdfCanvasViewer data={pdfData} filename={filename} />
          : <iframe title={filename || 'Document viewer'} src={url} style={{ flex: 1, border: 0, width: '100%' }} />}
      </div>
    </div>
  );
}
DocumentViewerModal.propTypes = {
  url: PropTypes.string,
  filename: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  // url is a streaming playback link for a <video>, not a blob: URL.
  video: PropTypes.bool,
};
